import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { fileService } from '../services/apiService';
import {
  ArrowLeft, RefreshCw, ImageOff, Upload, CheckCircle2,
  AlertTriangle, Loader2, ChevronDown, ChevronUp, RotateCcw,
  Image as ImageIcon, PackageOpen, FileText, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const MODULE_META = {
  orders:          { label: 'Orders',          icon: PackageOpen,  color: 'blue' },
  stock_estimates: { label: 'Stock Estimates',  icon: ImageIcon,    color: 'purple' },
  estimates:       { label: 'Sales Estimates',  icon: FileText,     color: 'amber' },
  lead_messages:   { label: 'Lead Messages',    icon: MessageSquare,color: 'rose' },
};

const COLOR = {
  blue:   { bg: 'bg-blue-500/10',   border: 'border-blue-500/20',   text: 'text-blue-400',   badge: 'bg-blue-500/20 text-blue-300' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-300' },
  amber:  { bg: 'bg-amber-500/10',  border: 'border-amber-500/20',  text: 'text-amber-400',  badge: 'bg-amber-500/20 text-amber-300' },
  rose:   { bg: 'bg-rose-500/10',   border: 'border-rose-500/20',   text: 'text-rose-400',   badge: 'bg-rose-500/20 text-rose-300' },
};

// ─── Single broken-image row ─────────────────────────────────────────────────
function BrokenImageRow({ module, record, brokenItem, onFixed }) {
  const [uploading, setUploading] = useState(false);
  const [fixed, setFixed] = useState(false);
  const fileRef = useRef(null);

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await fileService.uploadFile(file);
      await api.patch('/admin/fix-image', {
        module,
        id: record.id,
        field: brokenItem.field || 'images',
        old_url: brokenItem.url,
        new_url: result.url,
      });
      setFixed(true);
      toast.success('Image replaced successfully');
      onFixed(brokenItem.url);
    } catch (err) {
      toast.error('Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  if (fixed) return null;

  const filename = brokenItem.url.split('/').pop();

  return (
    <div
      data-testid={`broken-image-row-${filename}`}
      className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/5 border border-white/10 group"
    >
      {/* broken thumbnail placeholder */}
      <div className="w-10 h-10 rounded-md bg-white/5 border border-dashed border-white/20 flex items-center justify-center flex-shrink-0">
        <ImageOff className="w-4 h-4 text-white/30" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs text-white/50 font-mono truncate">{filename}</p>
        {brokenItem.field && (
          <p className="text-[10px] text-white/30 capitalize">{brokenItem.field.replace(/_/g,' ')}</p>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleReplace}
        data-testid={`file-input-${filename}`}
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`reupload-btn-${filename}`}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/70 hover:text-white text-xs font-medium transition-all disabled:opacity-50"
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? 'Uploading…' : 'Replace'}
      </button>
    </div>
  );
}

// ─── Record card (collapsible) ────────────────────────────────────────────────
function RecordCard({ module, record, onFixed }) {
  const [expanded, setExpanded] = useState(false);
  const [broken, setBroken] = useState(record.broken || (record.url ? [{ url: record.url, field: 'content' }] : []));

  const handleFixed = (oldUrl) => {
    setBroken(prev => prev.filter(b => b.url !== oldUrl));
    onFixed?.();
  };

  if (broken.length === 0) return null;

  const label = record.order_number || record.estimate_number || `Session ${record.session_id?.slice(-6)}`;
  const sub   = record.customer_name || record.title || '';

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        data-testid={`record-card-${record.id}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white">{label}</span>
          {sub && <span className="text-xs text-white/40">{sub}</span>}
          <span className="text-[11px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">
            {broken.length} missing
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/10 pt-3">
          {broken.map((b, i) => (
            <BrokenImageRow
              key={i}
              module={module}
              record={record}
              brokenItem={b}
              onFixed={handleFixed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Module section ───────────────────────────────────────────────────────────
function ModuleSection({ moduleKey, records, onRefresh }) {
  const meta  = MODULE_META[moduleKey];
  const c     = COLOR[meta.color];
  const Icon  = meta.icon;
  const [collapsed, setCollapsed] = useState(false);
  const [localRecords, setLocalRecords] = useState(records);

  useEffect(() => setLocalRecords(records), [records]);

  const handleFixed = useCallback(() => {
    // Trigger parent refresh after a short delay
    setTimeout(onRefresh, 500);
  }, [onRefresh]);

  const remaining = localRecords.filter(r => {
    const items = r.broken || (r.url ? [r.url] : []);
    return items.length > 0;
  });

  if (localRecords.length === 0) return null;

  return (
    <div className={`rounded-2xl border ${c.border} ${c.bg} p-4 space-y-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${c.text}`} />
          <span className="font-semibold text-white">{meta.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>
            {localRecords.length} records
          </span>
        </div>
        <button
          onClick={() => setCollapsed(v => !v)}
          className="text-white/40 hover:text-white/70 transition-colors"
          data-testid={`collapse-${moduleKey}`}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-2">
          {localRecords.map(record => (
            <RecordCard
              key={record.id}
              module={moduleKey}
              record={record}
              onFixed={handleFixed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MissingImagesPanel() {
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migStatus, setMigStatus] = useState(null);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/missing-images');
      setData(res.data);
    } catch {
      toast.error('Failed to load missing images data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  const runRecovery = async () => {
    setMigrating(true);
    try {
      await api.post('/admin/migrate-images');
      toast.info('Recovery scan started — checking object storage for recoverable images…');
      // Poll status
      pollRef.current = setInterval(async () => {
        const r = await api.get('/admin/migrate-images/status');
        setMigStatus(r.data);
        if (!r.data.running) {
          clearInterval(pollRef.current);
          setMigrating(false);
          const { done, missing } = r.data;
          if (done > 0) {
            toast.success(`Recovered ${done} image${done > 1 ? 's' : ''} from object storage!`);
          } else {
            toast.info(`Scan complete — ${missing} images not found in object storage.`);
          }
          await fetchData(); // refresh the list
        }
      }, 2000);
    } catch {
      toast.error('Recovery failed to start');
      setMigrating(false);
    }
  };

  const summary = data?.summary || {};
  const total   = summary.total_records || 0;
  const allFixed = !loading && total === 0;

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f0f0f]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="p-2 rounded-xl hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold">Missing Images Recovery</h1>
          <p className="text-xs text-white/40">Re-upload lost images across all modules</p>
        </div>
        <button
          onClick={fetchData}
          data-testid="refresh-btn"
          className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/50 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">

        {/* Recovery CTA */}
        <div className="rounded-2xl bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/20 p-4">
          <div className="flex items-start gap-3">
            <RotateCcw className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-300">Auto-Recover from Object Storage</p>
              <p className="text-xs text-white/50 mt-0.5">
                Some lost images may already be in object storage. Run this scan to restore them automatically — no re-uploading needed.
              </p>
              {migStatus && !migrating && (
                <p className="text-xs text-emerald-400 mt-1 font-medium">
                  Last scan: {migStatus.done} recovered · {migStatus.missing} not found
                </p>
              )}
              {migrating && migStatus && (
                <p className="text-xs text-white/50 mt-1">
                  Scanning… {migStatus.done} recovered so far
                </p>
              )}
            </div>
            <button
              onClick={runRecovery}
              disabled={migrating}
              data-testid="run-recovery-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold transition-all disabled:opacity-60 flex-shrink-0"
            >
              {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {migrating ? 'Scanning…' : 'Run Recovery'}
            </button>
          </div>
        </div>

        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(MODULE_META).map(([key, meta]) => {
              const count = summary[key] || 0;
              const c = COLOR[meta.color];
              const Icon = meta.icon;
              return (
                <div key={key} className={`rounded-xl p-3 border ${c.border} ${c.bg}`} data-testid={`stat-${key}`}>
                  <Icon className={`w-4 h-4 ${c.text} mb-1`} />
                  <p className="text-xl font-bold text-white">{count}</p>
                  <p className="text-[10px] text-white/40 leading-tight">{meta.label}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 gap-3 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Scanning all modules…</span>
          </div>
        )}

        {/* All clear */}
        {allFixed && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-lg font-semibold text-white">All images are healthy!</p>
            <p className="text-sm text-white/40">No broken image URLs found across any module.</p>
          </div>
        )}

        {/* Module sections */}
        {!loading && data && (
          <div className="space-y-4" data-testid="modules-container">
            {/* Lead Messages — note only message id, no record grouping */}
            {data.lead_messages?.length > 0 && (
              <ModuleSection
                moduleKey="lead_messages"
                records={data.lead_messages.map(m => ({
                  id: m.id,
                  session_id: m.session_id,
                  order_number: `Message · ${new Date(m.created_at).toLocaleDateString()}`,
                  broken: [{ url: m.url, field: 'content' }],
                }))}
                onRefresh={fetchData}
              />
            )}
            {['orders', 'stock_estimates', 'estimates'].map(key =>
              data[key]?.length > 0 ? (
                <ModuleSection key={key} moduleKey={key} records={data[key]} onRefresh={fetchData} />
              ) : null
            )}
          </div>
        )}

        {/* Warning about truly lost images */}
        {!loading && total > 0 && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-white/50 leading-relaxed">
              Images listed here were stored on local disk and may not be recoverable via auto-scan.
              Use <strong className="text-white/70">Run Recovery</strong> first — it may restore some automatically.
              For the rest, tap <strong className="text-white/70">Replace</strong> on any image to re-upload from your device.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
